import { FocusKeyManager, A11yModule as A11yModule$1 } from '@angular/cdk/a11y';
import * as i0 from '@angular/core';
import { EventEmitter, Directive, Output, HostListener, ContentChildren, NgModule } from '@angular/core';
import * as i1 from '@odx/angular';
import { DisabledController, WithDisabledState, WithTabIndex, CoreModule } from '@odx/angular';
import { __decorate } from 'tslib';
import { CSSComponent } from '@odx/angular/internal';
import { injectElement, deferFn } from '@odx/angular/utils';

/**
 * Directive that makes an element interactive, responding to click, Enter key, and Space key events.
 * It automatically handles the disabled state through `DisabledController`, inhibiting interactions when disabled.
 * This directive can be useful for creating custom buttons or interactive elements that are not natively interactive,
 * such as divs or custom components.
 *
 * @Directive
 * @CSSComponent cdk-interactive - A CSS Component class that can be targeted for styling interactive elements.
 * @selector '[odxCdkInteractive]' - Applies this directive to any element with the 'odxCdkInteractive' attribute.
 * @hostDirectives {WithDisabledState, WithTabIndex} - Applies disabled state and tab index handling.
 * @host role='button' - Assigns a role of 'button' to the host element for accessibility.
 *
 * @example
 * Applying the directive to a div element to make it interactive:
 * ```html
 * <div odxCdkInteractive (odxCdkInteractive)="onInteract($event)">
 *   Click or press Enter/Space to interact
 * </div>
 * ```
 *
 * @example
 * Using the directive on a custom Angular component
 * ```html
 * <app-custom-button odxCdkInteractive (odxCdkInteractive)="onCustomButtonInteract($event)">
 * </app-custom-button>
 * ```
 */
let InteractiveDirective = class InteractiveDirective {
    constructor() {
        this.disabledController = DisabledController.inject();
        this.element = injectElement();
        /**
         * Emits an event when the element is interacted with via click, Enter key, or Space key.
         *
         * @emits {MouseEvent | KeyboardEvent} - The event that triggered the interaction.
         */
        this.interact = new EventEmitter();
    }
    handleEvents(event) {
        if (this.disabledController?.disabled)
            return;
        this.interact.next(event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: InteractiveDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: InteractiveDirective, isStandalone: true, selector: "[odxCdkInteractive]", outputs: { interact: "odxCdkInteractive" }, host: { attributes: { "role": "button" }, listeners: { "click": "handleEvents($event)", "keyup.enter": "handleEvents($event)", "keyup.space": "handleEvents($event)" } }, hostDirectives: [{ directive: i1.WithDisabledState }, { directive: i1.WithTabIndex }], ngImport: i0 }); }
};
InteractiveDirective = __decorate([
    CSSComponent('cdk-interactive')
], InteractiveDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: InteractiveDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[odxCdkInteractive]',
                    standalone: true,
                    hostDirectives: [WithDisabledState, WithTabIndex],
                    host: {
                        role: 'button',
                    },
                }]
        }], propDecorators: { interact: [{
                type: Output,
                args: ['odxCdkInteractive']
            }], handleEvents: [{
                type: HostListener,
                args: ['click', ['$event']]
            }, {
                type: HostListener,
                args: ['keyup.enter', ['$event']]
            }, {
                type: HostListener,
                args: ['keyup.space', ['$event']]
            }] } });

/**
 * Directive to make an element focusable as part of a list, integrating with Angular CDK's FocusKeyManager.
 * It listens to the disabled state controlled by `DisabledController` to manage focus correctly based on
 * the enabled state of the element.
 *
 * @example
 * In a component template, apply this directive to list items that should be focusable and managed by a FocusKeyManager:
 * ```html
 * @for(item of items; track item) {
 * <li odxListFocusManagerOption>
 *   {{ item }}
 * </li>
 * }
 * ```
 */
class ListFocusManagerOptionDirective {
    constructor() {
        this.disabledController = DisabledController.inject();
        this.element = injectElement();
    }
    /**
     * Reflects the disabled state of the element, managed by `DisabledController`.
     * If disabled, the element is not focusable.
     *
     * @returns {boolean} True if the directive's host element is disabled.
     */
    get disabled() {
        return !!this.disabledController?.disabled;
    }
    /**
     * Focuses the host element. If the element is disabled, it will not be focused.
     */
    focus() {
        this.element.nativeElement.focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ListFocusManagerOptionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: ListFocusManagerOptionDirective, isStandalone: true, selector: "[odxListFocusManagerOption]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ListFocusManagerOptionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[odxListFocusManagerOption]',
                    standalone: true,
                }]
        }] });

/**
 * Directive that sets up keyboard navigation for a list or a group of focusable elements.
 * Utilizes `FocusKeyManager` from Angular CDK to manage focus among items.
 * It automatically skips disabled items and wraps around when navigating past the first or last item.
 * Handles keydown events to navigate through the focusable options.
 *
 * @example
 * In a component template, use this directive on a container element and `odxListFocusManagerOption`
 * on each focusable list item:
 * ```html
 * <ul odxListFocusManager>
 *   @for(item of items; track item) {
 *   <li odxListFocusManagerOption>
 *     {{ item }}
 *   </li>
 *   }
 * </ul>
 * ```
 */
class ListFocusManagerDirective {
    constructor() {
        this.keyManager = null;
    }
    ngAfterContentInit() {
        this.keyManager = new FocusKeyManager(this.options)
            .withHomeAndEnd()
            .withWrap()
            .skipPredicate((item) => item.disabled);
        deferFn(() => this.keyManager?.setFirstItemActive());
    }
    ngOnDestroy() {
        this.keyManager?.destroy();
        this.keyManager = null;
    }
    onKeyDown(event) {
        this.keyManager?.onKeydown(event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ListFocusManagerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: ListFocusManagerDirective, isStandalone: true, selector: "[odxListFocusManager]", host: { listeners: { "keydown": "onKeyDown($event)" } }, queries: [{ propertyName: "options", predicate: ListFocusManagerOptionDirective }], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ListFocusManagerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[odxListFocusManager]',
                    standalone: true,
                }]
        }], propDecorators: { options: [{
                type: ContentChildren,
                args: [ListFocusManagerOptionDirective]
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

const modules = [ListFocusManagerDirective, ListFocusManagerOptionDirective, InteractiveDirective];
class A11yModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: A11yModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: A11yModule, imports: [A11yModule$1, ListFocusManagerDirective, ListFocusManagerOptionDirective, InteractiveDirective], exports: [CoreModule, A11yModule$1, ListFocusManagerDirective, ListFocusManagerOptionDirective, InteractiveDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: A11yModule, imports: [A11yModule$1, CoreModule, A11yModule$1] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: A11yModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [A11yModule$1, ...modules],
                    exports: [CoreModule, A11yModule$1, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { A11yModule, InteractiveDirective, ListFocusManagerDirective, ListFocusManagerOptionDirective };
